// Per-module internal cost line items for the internal split PDF. Each module's
// pre-margin cost is broken into the lines that sum to it, with a short note
// showing the arithmetic (volume x unit cost, per-source onboarding, crew days),
// mirroring how the price section derives each line. Cost figures come from the
// backend breakdown; nothing is recomputed here beyond the display note.

type Detail = Record<string, unknown>;

// One cost-contributing line under a module: its label, cost, and a derivation
// note (empty for pass-through lines with no per-unit arithmetic).
export interface CostLine {
  label: string;
  cost: number;
  note: string;
}

const WELDING_ID = 'welding';

const num = (v: unknown): number => (typeof v === 'number' ? v : 0);

const money = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const count = (n: number): string => n.toLocaleString('en-US');

// The derivation note for one cost line, drawn from its build-up detail. Falls
// back to an empty string for pass-through lines (add-ons, mobilization).
function costNote(detail: Detail | undefined): string {
  if (!detail) return '';
  const volume = num(detail.volume);
  if ('costPerPipe' in detail && volume > 0) {
    return `${count(volume)} pipes x ${money(num(detail.costPerPipe))}/pipe`;
  }
  if ('costPerInspection' in detail && volume > 0) {
    return `${count(volume)} insp x ${money(num(detail.costPerInspection))}/insp`;
  }
  if ('costPerJoint' in detail && volume > 0) {
    return `${count(volume)} joints x ${money(num(detail.costPerJoint))}/joint`;
  }
  const sources = detail.mills ?? detail.vendors ?? detail.contractors;
  if (Array.isArray(sources)) {
    const n = sources.length;
    return `${count(n)} source${n === 1 ? '' : 's'}, cost-modelled`;
  }
  if (detail.mode === 'service') {
    return `${count(num(detail.days))} days x ${money(num(detail.dayCost))}/day`;
  }
  return '';
}

// Cost lines for a data module: the one-time and recurring components that carry
// a positive cost, each with its derivation note.
function componentCostLines(details: Detail): CostLine[] {
  const lines: CostLine[] = [];
  const oneTime = details.oneTime as Detail | undefined;
  const oneTimeComponents = (oneTime?.components as Detail[] | undefined) ?? [];
  for (const component of oneTimeComponents) {
    const cost = num(component.cost);
    if (cost <= 0) continue;
    lines.push({
      label: String(component.label ?? ''),
      cost,
      note: costNote(component.detail as Detail | undefined),
    });
  }
  const recurring = details.recurring as Detail | undefined;
  const recurringComponents = (recurring?.components as Detail[] | undefined) ?? [];
  for (const component of recurringComponents) {
    const cost = num(component.contractCost);
    if (cost <= 0) continue;
    lines.push({ label: String(component.label ?? ''), cost, note: '' });
  }
  return lines;
}

// Cost lines for welding come pre-built by the backend (it owns the rates and
// quantities), each with a quantity x rate note so the figure can be verified
// against the config. Hardware is the one-time purchase; internet/cloud are per
// connected unit per month; services are billable hours/days x rate. Software is
// already developed, so it carries no cost and no line.
function weldingCostLines(details: Detail): CostLine[] {
  const lines = details.costLines as Detail[] | undefined;
  if (!Array.isArray(lines)) return [];
  return lines
    .map(line => ({
      label: String(line.label ?? ''),
      cost: num(line.cost),
      note: String(line.note ?? ''),
    }))
    .filter(line => line.cost > 0);
}

// Build the cost lines for one module from its breakdown details.
export function buildCostLines(
  productId: string,
  details: Detail | undefined
): CostLine[] {
  if (!details) return [];
  if (productId === WELDING_ID) return weldingCostLines(details);
  return componentCostLines(details);
}
